import { useEffect, useRef, useState } from 'react'
import { trpc, type RouterOutput } from '@/config/trpc'
import { PlatformFilter } from '@/components/analytics/platform-filter'
import { type PublishablePlatformType } from '@backend/lib/db/schema/platform'
import {
  usePlatformSyncIssues,
  formatPlatformList,
  type SyncIssueKind,
} from '@/lib/platform-sync-status'
import { SummaryCards } from '@/components/analytics/summary-cards'
import { BestTimeToPostCard } from '@/components/analytics/breakdowns/best-time-to-post-card'
import { FormatImpactCard } from '@/components/analytics/breakdowns/format-impact-card'
import { EngagementQualityCard } from '@/components/analytics/breakdowns/engagement-quality-card'
import { CaptionSweetSpotCard } from '@/components/analytics/breakdowns/caption-sweet-spot-card'
import { PerformanceDecayCard } from '@/components/analytics/breakdowns/performance-decay-card'
import { HashtagEffectCard } from '@/components/analytics/breakdowns/hashtag-effect-card'
import { HookLengthCard } from '@/components/analytics/breakdowns/hook-length-card'
import { CaptionCraftCard } from '@/components/analytics/breakdowns/caption-craft-card'
import { ContentTypeImpactCard } from '@/components/analytics/breakdowns/content-type-impact-card'
import { ContentMixCard } from '@/components/analytics/breakdowns/content-mix-card'
import {
  AIInsightsHeader,
  AIInsightsSuggestion,
  useAnalyticsInsights,
} from '@/components/analytics/breakdowns/ai-insights'
import { TrackedPostList } from '@/components/analytics/tracked-post-list'
import { AccountOverview } from '@/components/analytics/account-overview'
import { ImportPostsModal } from '@/components/my-posts/import-posts-modal'
import { TestSyncDialog } from '@/components/analytics/test-sync-dialog'
import { Button } from '@/components/ui/shadcn/button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/shadcn/tabs'
import { AlertTriangle, BarChart3, Users } from 'lucide-react'
import env from '@/config/env'
import { Link } from 'react-router'

type PostItem = RouterOutput['analytics']['overview']['posts'][number]

const PAGE_SIZE = 50

const ISSUE_COPY: Record<SyncIssueKind, { label: string; cta?: string }> = {
  needs_reauth: {
    label: 'an expired session that can no longer be refreshed',
    cta: 'Reconnect in settings',
  },
  missing_scopes: {
    label: 'missing analytics permissions',
    cta: 'Reauthorize with analytics permission',
  },
  rate_limited: { label: 'a rate limit; the next sync will retry' },
  error: { label: 'a sync error; the next sync will retry' },
}

export function AnalyticsPage() {
  const [platforms, setPlatforms] = useState<PublishablePlatformType[]>([])
  const [offset, setOffset] = useState(0)
  const [loadedPosts, setLoadedPosts] = useState<PostItem[]>([])
  const [importOpen, setImportOpen] = useState(false)
  // Track which platform filter was active when data was last applied,
  // so we can discard stale responses after filter changes. Sorted+joined
  // gives a stable key even though `platforms` is a new array each render.
  const platformKey = [...platforms].sort().join(',')
  const lastAppliedRef = useRef<{ platformKey: string; offset: number; data: unknown }>(null)

  const syncIssues = usePlatformSyncIssues()
  const issuesByKind = syncIssues.reduce<Record<SyncIssueKind, PublishablePlatformType[]>>(
    (acc, { platform, kind }) => {
      acc[kind] = [...(acc[kind] ?? []), platform]
      return acc
    },
    {} as Record<SyncIssueKind, PublishablePlatformType[]>,
  )

  const { data, isLoading, isFetching, error } = trpc.analytics.overview.useQuery({
    platforms: platforms.length > 0 ? platforms : undefined,
    limit: PAGE_SIZE,
    offset,
  })

  // Accumulate posts as pages load, ignoring stale responses from previous filters
  useEffect(() => {
    if (!data) return
    if (lastAppliedRef.current?.data === data) return
    if (lastAppliedRef.current && lastAppliedRef.current.platformKey !== platformKey) return
    lastAppliedRef.current = { platformKey, offset, data }
    if (offset === 0) {
      setLoadedPosts(data.posts)
    } else {
      setLoadedPosts((prev) => [...prev, ...data.posts])
    }
  }, [data, offset, platformKey])

  const handlePlatformsChange = (next: PublishablePlatformType[]) => {
    setPlatforms(next)
    setOffset(0)
    setLoadedPosts([])
    lastAppliedRef.current = null
  }

  // Single streaming subscription shared by header + suggestion components.
  // Hook owns the fetch lifecycle; the components are pure renderers.
  const aiInsights = useAnalyticsInsights(platforms)

  const handleLoadMore = () => {
    setOffset((prev) => prev + PAGE_SIZE)
  }

  const total = data?.total ?? 0
  const hasMore = loadedPosts.length < total

  return (
    <div className="mx-auto w-full max-w-5xl px-6 py-8">
      <div className="mb-8 flex items-start justify-between">
        <div>
          <h1 className="text-foreground text-2xl font-semibold tracking-tight">Analytics</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Track post performance across all platforms
          </p>
        </div>
        {env.MODE !== 'production' && <TestSyncDialog />}
      </div>

      {syncIssues.length > 0 && (
        <div className="mb-6 flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          {(Object.entries(issuesByKind) as [SyncIssueKind, PublishablePlatformType[]][]).map(
            ([kind, names]) => {
              const { label, cta } = ISSUE_COPY[kind]
              return (
                <div key={kind} className="flex items-start gap-3">
                  <AlertTriangle
                    size={16}
                    className="mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-400"
                  />
                  <p>
                    Analytics sync is paused for{' '}
                    <span className="font-medium">{formatPlatformList(names)}</span> due to {label}.
                    {cta && (
                      <>
                        {' '}
                        <Link
                          to="/account/social-media"
                          className="font-medium underline underline-offset-2">
                          {cta}
                        </Link>
                      </>
                    )}
                  </p>
                </div>
              )
            },
          )}
        </div>
      )}

      <div className="mb-6">
        <PlatformFilter selected={platforms} onChange={handlePlatformsChange} />
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <div className="border-primary/30 border-t-primary h-7 w-7 animate-spin rounded-full border-2" />
        </div>
      )}

      {error && (
        <div className="flex flex-col items-center gap-2 py-16 text-center">
          <p className="text-foreground font-medium">Failed to load analytics</p>
          <p className="text-muted-foreground text-sm">{error.message}</p>
        </div>
      )}

      {data && (
        <>
          <div className="mb-6">
            <SummaryCards data={data} />
          </div>

          <AIInsightsHeader platforms={platforms} state={aiInsights} />

          <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
            <BestTimeToPostCard platforms={platforms} />
            <EngagementQualityCard platforms={platforms} />
            <PerformanceDecayCard platforms={platforms} />
          </div>

          {/* Content cards stay visible across all filter scopes — comparing
              "personal stories" engagement across platforms IS meaningful in
              a way that comparing IG reels vs. YouTube videos isn't. Mix vs.
              Impact paired together is the most actionable read in the
              dashboard. */}
          <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <ContentTypeImpactCard platforms={platforms} />
            <ContentMixCard platforms={platforms} />
          </div>

          {/* Craft signals (format / caption length / hashtags / hooks /
              caption craft) only render under a single-platform filter — the
              grouped multi-platform layout was harder to read than informative.
              Cross-platform comparisons of these signals are also misleading
              (an IG reel ≠ a YouTube video), so the AI prompt makes the same
              call. */}
          {platforms.length === 1 && (
            <>
              <div className="mb-3 grid grid-cols-1 gap-3 lg:grid-cols-3">
                <FormatImpactCard platforms={platforms} />
                <HookLengthCard platforms={platforms} />
                <CaptionCraftCard platforms={platforms} />
              </div>

              <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
                <CaptionSweetSpotCard platforms={platforms} />
                <HashtagEffectCard platforms={platforms} />
              </div>
            </>
          )}

          <div className="mb-8">
            <AIInsightsSuggestion platforms={platforms} state={aiInsights} />
          </div>

          <Tabs defaultValue="posts">
            <TabsList className="mb-5">
              <TabsTrigger value="posts" className="gap-1.5">
                <BarChart3 size={13} />
                Posts
              </TabsTrigger>
              <TabsTrigger value="accounts" className="gap-1.5">
                <Users size={13} />
                Accounts
              </TabsTrigger>
            </TabsList>

            <TabsContent value="posts">
              <TrackedPostList posts={loadedPosts} onImport={() => setImportOpen(true)} />
              {hasMore && (
                <div className="mt-6 flex justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLoadMore}
                    disabled={isFetching}
                    className="text-muted-foreground">
                    {isFetching ? (
                      <span className="flex items-center gap-2">
                        <span className="border-primary/30 border-t-primary h-3.5 w-3.5 animate-spin rounded-full border-2" />
                        Loading...
                      </span>
                    ) : (
                      `Show more (${loadedPosts.length} of ${total})`
                    )}
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="accounts">
              <AccountOverview platforms={platforms} />
            </TabsContent>
          </Tabs>
        </>
      )}
      <ImportPostsModal open={importOpen} onOpenChange={setImportOpen} />
    </div>
  )
}
